/** 28.09: шторки правок админа зала. Любая правка — заявка в ОО (владелец: «всё через ОО»):
 *  выбор → подтверждение с необязательной причиной (она уходит ОО и остаётся в истории).
 *  Кого поставить — с тем же квадратиком, что на доске ОО: свободен ли по графику всю игру. */
import { useState } from "react";
import { SheetDialog, TextPad } from "../../components/shared";
import { freeForGame, type HallDay, type HallExtra, type HallGame, type HallOp, type ReqIn } from "../../hooks/useHallDay";
import h from "./hallAdmin.module.css";

type Mc = { id: string; name: string };
export type Sheet =
  | { t: "crew"; g: HallGame; op: HallOp }
  | { t: "add"; g: HallGame }
  | { t: "moveTo"; g: HallGame; op: HallOp }
  | { t: "mcOp"; g: HallGame; mc: Mc; op: HallOp }
  | { t: "mcAdd"; g: HallGame; mc: Mc }
  | { t: "mcNew"; g: HallGame }
  | { t: "mcNewOp"; g: HallGame; mc: Mc }
  | { t: "confirm"; req: ReqIn; text: string };

export function HallSheets({ sheet, set, day, extra, onSend }: {
  sheet: Sheet | null; set: (s: Sheet | null) => void; day: HallDay; extra: HallExtra | null;
  onSend: (r: ReqIn) => Promise<string | null>;
}) {
  if (!sheet) return null;
  const close = () => set(null);
  const ask = (req: ReqIn, text: string) => set({ t: "confirm", req, text });
  const pool = extra?.pool ?? [];
  const title =
    sheet.t === "crew" ? `${sheet.op.name} · ${sheet.g.quest} ${sheet.g.time}`
    : sheet.t === "add" ? `Кого поставить на ${sheet.g.quest} ${sheet.g.time}`
    : sheet.t === "moveTo" ? `Куда переставить ${sheet.op.name}`
    : sheet.t === "mcOp" ? `${sheet.op.name} · МК «${sheet.mc.name}»`
    : sheet.t === "mcAdd" || sheet.t === "mcNewOp" ? `Кто ведёт МК «${sheet.mc.name}»`
    : sheet.t === "mcNew" ? `Какой МК добавить в ${sheet.g.quest} ${sheet.g.time}` : "Заявка в ОО";
  const opList = (g: HallGame, exclude: string[], pick: (op: HallOp) => void, extraRow?: { label: string; onClick: () => void }) => (
    <div className={h.list}>
      {extraRow ? <button type="button" className={h.row} onClick={extraRow.onClick}>{extraRow.label}</button> : null}
      {pool.length === 0 ? <div className={h.note}>Список сотрудников не загрузился</div> : null}
      {[...pool].filter((p) => !exclude.includes(p.id) && !p.board_hidden)   /* 16.10: служебные — «не показывать в расстановке» (HR) */
        .map((p) => ({ p, ok: freeForGame(p, g.time, g.duration) }))
        .sort((a, b) => Number(b.ok) - Number(a.ok) || a.p.name.localeCompare(b.p.name, "ru"))
        .map(({ p, ok }) => (
          <button key={p.id} type="button" className={h.row} onClick={() => pick(p)}>
            <span className={`${h.sq} ${ok ? h.sqOk : h.sqNo}`} aria-label={ok ? "по графику свободен" : "по графику занят"} />
            <span className={h.rowName}>{p.name}</span>
            {p.busy.length ? <span className={h.rowBusy}>занят: {p.busy.join(", ")}</span> : null}
          </button>
        ))}
    </div>
  );
  return (
    <SheetDialog open onDismiss={close} ariaLabel={title} dragHandle maxHeight="80dvh">
      <div className={h.sheet}>
        <div className={h.sheetTitle}>{title}</div>
        {sheet.t === "crew" ? (
          <div className={h.list}>
            <button type="button" className={h.row} onClick={() => set({ t: "moveTo", g: sheet.g, op: sheet.op })}>🔀 Переставить на другую игру…</button>
            <button type="button" className={h.row} onClick={() => ask({ kind: "remove", booking_id: sheet.g.booking_id, operator_id: sheet.op.id }, `➖ Снять ${sheet.op.name} с ${sheet.g.quest} ${sheet.g.time}`)}>➖ Снять с игры</button>
          </div>
        ) : sheet.t === "add" ? opList(sheet.g, sheet.g.crew.map((o) => o.id), (op) => ask({ kind: "add", booking_id: sheet.g.booking_id, operator_id: op.id }, `➕ ${op.name} → ${sheet.g.quest} ${sheet.g.time}`))
        : sheet.t === "moveTo" ? (
          <div className={h.list}>
            {day.games.filter((x) => x.booking_id !== sheet.g.booking_id).map((x) => {
              const here = x.crew.some((o) => o.id === sheet.op.id);
              const p = pool.find((o) => o.id === sheet.op.id);
              return (
                <button key={x.booking_id} type="button" className={h.row} disabled={here}
                  onClick={() => ask({ kind: "move", from_booking_id: sheet.g.booking_id, booking_id: x.booking_id, operator_id: sheet.op.id }, `🔀 ${sheet.op.name}: ${sheet.g.quest} ${sheet.g.time} → ${x.quest} ${x.time}`)}>
                  {p ? <span className={`${h.sq} ${freeForGame(p, x.time, x.duration) ? h.sqOk : h.sqNo}`} /> : null}
                  <span className={h.rowName}>{x.time} {x.quest}</span>
                  <span className={h.rowBusy}>{here ? "уже на ней" : `${x.crew.length}/${x.need || "?"} оп.`}</span>
                </button>
              );
            })}
          </div>
        ) : sheet.t === "mcOp" ? (
          <div className={h.list}>
            <button type="button" className={h.row} onClick={() => ask({ kind: "mc_remove", booking_id: sheet.g.booking_id, mc_id: sheet.mc.id, operator_id: sheet.op.id }, `🎓 Снять ${sheet.op.name} с МК «${sheet.mc.name}» · ${sheet.g.quest} ${sheet.g.time}`)}>➖ Снять с МК</button>
          </div>
        ) : sheet.t === "mcAdd" ? opList(sheet.g, sheet.g.mcs.find((m) => m.id === sheet.mc.id)?.ops.map((o) => o.id) ?? [],
            (op) => ask({ kind: "mc_add", booking_id: sheet.g.booking_id, mc_id: sheet.mc.id, operator_id: op.id }, `🎓 ${op.name} ведёт МК «${sheet.mc.name}» · ${sheet.g.quest} ${sheet.g.time}`))
        : sheet.t === "mcNew" ? (
          <div className={h.list}>
            {(extra?.mc_catalog ?? []).filter((m) => !sheet.g.mcs.some((x) => x.id === m.id)).map((m) => (
              <button key={m.id} type="button" className={h.row} onClick={() => set({ t: "mcNewOp", g: sheet.g, mc: m })}>🎓 {m.name}</button>
            ))}
          </div>
        ) : sheet.t === "mcNewOp" ? opList(sheet.g, [], (op) => ask({ kind: "mc_new", booking_id: sheet.g.booking_id, mc_id: sheet.mc.id, operator_id: op.id }, `🎓 Добавить МК «${sheet.mc.name}» в ${sheet.g.quest} ${sheet.g.time} · ведёт ${op.name}`),
            { label: "Без ведущего — только добавить МК", onClick: () => ask({ kind: "mc_new", booking_id: sheet.g.booking_id, mc_id: sheet.mc.id }, `🎓 Добавить МК «${sheet.mc.name}» в ${sheet.g.quest} ${sheet.g.time}`) })
        : <Confirm text={sheet.text} onCancel={close} onSend={async (note) => { const err = await onSend({ ...sheet.req, note }); if (!err) close(); return err; }} />}
      </div>
    </SheetDialog>
  );
}

function Confirm({ text, onCancel, onSend }: { text: string; onCancel: () => void; onSend: (note: string) => Promise<string | null> }) {
  const [note, setNote] = useState(""); const [pad, setPad] = useState(false);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  return (
    <div className={h.confirm}>
      <div className={h.confirmText}>{text}</div>
      <button type="button" className={h.reason} data-pad-host="" onClick={() => setPad(true)}>
        <span className={h.lbl}>Причина для ОО</span>{note || <span className={h.note}>необязательно — тап, чтобы написать</span>}
      </button>
      {err ? <div className={h.warn}>{err}</div> : null}
      <div className={h.confirmActs}>
        <button type="button" className={h.accept} disabled={busy} onClick={() => { setBusy(true); setErr(null); void onSend(note).then((e) => { setErr(e); setBusy(false); }); }}>
          {busy ? "…" : "Отправить в ОО"}
        </button>
        <button type="button" className={h.ghost} onClick={onCancel}>Отмена</button>
      </div>
      <div className={h.note}>Изменится после одобрения ОО. Заявка и решение останутся в истории.</div>
      <TextPad open={pad} label="Причина для ОО" multiline maxLength={200} value={note} onCommit={setNote} onClose={() => setPad(false)} />
    </div>
  );
}
